// Temporary premium access utility
// All users get premium access until August 10th, 2025

const PREMIUM_ACCESS_END_DATE = new Date('2025-08-10T23:59:59');

/**
 * Check if current date is before the premium access end date
 * @returns {boolean} True if premium access is still active
 */
export const isTemporaryPremiumActive = () => {
    return new Date() < PREMIUM_ACCESS_END_DATE;
};

/**
 * Get the effective account type considering temporary premium access
 * @param {string} originalAccountType - The user's original account type
 * @returns {string} The effective account type (considering temporary premium)
 */
export const getEffectiveAccountType = (originalAccountType) => {
    if (isTemporaryPremiumActive()) {
        // During temporary premium period, all users get premium access
        // Unless they already have pro, keep their pro status
        return originalAccountType === 'pro' ? 'pro' : 'standard';
    }
    return originalAccountType;
};

/**
 * Check if user should see the premium unlock announcement
 * @param {Object} user - Firebase user object
 * @returns {boolean} True if user should see the announcement
 */
export const shouldShowPremiumAnnouncement = (user) => {
    if (!user || !isTemporaryPremiumActive()) {
        return false;
    }
    
    // Check if user has already seen the announcement
    const hasSeenAnnouncement = localStorage.getItem(`premiumAnnouncement_${user.uid}`);
    return !hasSeenAnnouncement;
};

/**
 * Mark that user has seen the premium announcement
 * @param {Object} user - Firebase user object
 */
export const markPremiumAnnouncementSeen = (user) => {
    if (user) {
        localStorage.setItem(`premiumAnnouncement_${user.uid}`, 'true');
    }
};

/**
 * Get formatted end date for display
 * @param {boolean} isFrench - Whether to use French formatting
 * @returns {string} Formatted date string
 */
export const getPremiumAccessEndDateFormatted = (isFrench = false) => {
    return PREMIUM_ACCESS_END_DATE.toLocaleDateString(
        isFrench ? 'fr-CA' : 'en-CA',
        {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        }
    );
};

/**
 * Reset the premium announcement for a user (for testing purposes)
 * @param {Object} user - Firebase user object
 */
export const resetPremiumAnnouncement = (user) => {
    if (user) {
        localStorage.removeItem(`premiumAnnouncement_${user.uid}`);
    }
};

/**
 * Reset premium announcement for all users (clears all announcement flags)
 */
export const resetAllPremiumAnnouncements = () => {
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
        if (key.startsWith('premiumAnnouncement_')) {
            localStorage.removeItem(key);
        }
    });
}; 